import { z } from "zod";
import { zDiaCivil, zId, zInstanteUtc, zTipoItem } from "./dominio.js";
import { somarDias } from "./dia.js";
import { zParametrosDeReincidencia } from "./reincidencia.js";
import { zAuditoria } from "./srs.js";
/**
 * Tela Revisar — as duas listas do ciclo de erro (tela 3 do SPEC).
 *
 * São coisas diferentes e não devem virar uma só: a **fila de erradas** é
 * dívida (entrou ao errar, só sai ao acertar — RN-11), e a **revisão do dia** é
 * calendário (o SM-2 marcou para hoje — RN-13). Um item pode estar nas duas, e
 * juntá-las esconderia justamente esse caso.
 */
const zItemNaLista = z.object({
    itemId: zId,
    tipo: zTipoItem,
    /** Cortado: a lista é para reconhecer o item, não para responder. */
    enunciado: z.string(),
    categoria: z.array(z.string()),
    dificuldade: z.string().nullable(),
});
export const zErradaPendente = zItemNaLista.extend({
    /**
     * Quantas vezes reincidiu **enquanto na fila**. Continua sendo o dado
     * (RN-12), mas não ordena mais nada: quem ordena é o `score` (RN-45).
     */
    reincidencias: z.number().int(),
    primeiroErroEm: zInstanteUtc,
    ultimoErroEm: zInstanteUtc,
    /*
     * Os números crus da RN-45, ao lado do item, e o score no detalhe. É a mesma
     * disciplina da RN-32: mostrar a conta, não só o resultado. Sem `erros` e
     * `tentativas` na tela, a ordem da fila seria um oráculo.
     */
    /** Respostas erradas do item, na vida inteira dele. */
    erros: z.number().int().nonnegative(),
    /** Respostas do item, certas e erradas. */
    tentativas: z.number().int().nonnegative(),
    /** Dias inteiros desde a última tentativa. Desempata a ordem. */
    diasParado: z.number().int().nonnegative(),
    /** `(erros + alfa) / (tentativas + alfa + beta)`. Ordena a lista. */
    score: z.number(),
});
export const zDevidaHoje = zItemNaLista.extend({
    proximaRevisao: zDiaCivil,
    /** Dias de atraso. Zero quando vence hoje. */
    atrasoDias: z.number().int(),
    intervaloDias: z.number().int(),
    fatorFacilidade: z.number(),
    lapsos: z.number().int(),
    /** A conta da última revisão, para o intervalo ser conferível aqui também. */
    auditoria: zAuditoria.nullable(),
});
export const zPainelDeRevisao = z.object({
    erradas: z.array(zErradaPendente),
    /**
     * Os pesos com que os scores acima foram calculados (RN-45). Vão junto porque
     * a tela mostra a conta, e a conta sem os parâmetros não é conferível.
     */
    reincidencia: zParametrosDeReincidencia,
    devidas: z.array(zDevidaHoje),
    /** Quantos itens já têm agendamento — o resto entra no SRS na primeira resposta. */
    agendados: z.number().int(),
    /** Itens da trilha que ainda não têm agendamento nenhum. */
    semAgendamento: z.number().int(),
    /**
     * Quantas revisões estão vencidas **ao todo**, antes do teto do dia.
     *
     * `devidas` já vem cortada em `maximoRevisoesDia`. Sem este número a tela não
     * teria como dizer que há mais esperando, e uma lista que para em 30 sem
     * explicar parece lista que acabou.
     */
    devidasAoTodo: z.number().int(),
    /** O teto em vigor, que é configuração da trilha (invariante 4). */
    maximoRevisoesDia: z.number().int().positive(),
    hoje: zDiaCivil,
});
/**
 * O plano da redistribuição única — **puro, e por isso conferível**.
 *
 * O teto diário conserta o futuro: a fila do dia para em N e o excedente
 * reaparece amanhã. Ele não desmonta o que já se acumulou — com 173 vencidas e
 * teto de 30, são seis dias mostrando sempre as mesmas 30 mais atrasadas. Este
 * plano espalha o acúmulo em dias consecutivos a partir de hoje, começando pelas
 * mais atrasadas e contando o que **já** está marcado em cada dia: espalhar por
 * cima de dias ocupados só trocaria o pico de lugar.
 *
 * A execução — ler o banco, gravar, guardar o vencimento original — mora em
 * `scripts/redistribuir-revisoes.mjs`. Aqui fica só a decisão, que é a parte que
 * pode estar errada sem ninguém ver.
 */
export function planoDeRedistribuicao(vencidas, cargaFutura, hoje, teto) {
    if (teto < 1)
        return [];
    // Dentro do teto não há acúmulo: a fila de um dia já cabe num dia.
    if (vencidas.length <= teto)
        return [];
    const emOrdem = [...vencidas].sort((a, b) => a.proximaRevisao.localeCompare(b.proximaRevisao) || a.id.localeCompare(b.id));
    const carga = new Map(cargaFutura);
    const plano = [];
    let dia = hoje;
    for (const v of emOrdem) {
        while ((carga.get(dia) ?? 0) >= teto)
            dia = somarDias(dia, 1);
        carga.set(dia, (carga.get(dia) ?? 0) + 1);
        plano.push({ id: v.id, de: v.proximaRevisao, para: dia });
    }
    return plano;
}
//# sourceMappingURL=revisao.js.map